// §26.7 / §16 — the client half of multiplayer: six calls and no state.
//
// The same shape as `kernel/chat`, and the separation is the same argument: `kernel/social` owns a
// connection and two timers, and this owns nothing. Every method is a request; there is no cache,
// no subscription and nothing to stop. It lives in `kernel/` because §6.5 keeps `apps/**` away from
// `fetch`, not because it holds anything.
//
// **The game state is not here.** `gamekit/net.ts` holds the engine and the log and knows how to
// rebuild one from the other (§26.7); this is the transport underneath it. Keeping them apart is
// what lets the substrate be tested against thirty lines of fake server rather than against a
// kernel — and it is why a title receives a *capability*, never this.

import type { PlayPayload, PlayResult, PlaySession } from '@hangar/shared';

export interface PlayServiceOptions {
  /** Injected in tests; the real one in production. */
  fetch?: typeof globalThis.fetch;
}

export interface PlayService {
  /** §16 — start one, which also mints the invitation the peer's card is drawn from (§26.4). */
  create(gameId: string, peer: string): Promise<PlaySession>;
  /** Every session of the caller's that is still `invited` or `active`. */
  live(): Promise<PlaySession[]>;
  /** One session, with its log from `since` onwards. `since` is exclusive. */
  read(id: string, since?: number): Promise<PlaySession>;
  /**
   * Post a move. `'conflict'` is §16's 409 — a seq gap or the wrong player.
   *
   * A 409 is answered rather than thrown, because §26.8 F12 makes it the ordinary case: the caller
   * re-reads and rebuilds, and only a *second* failure is worth an exception.
   */
  move(id: string, seq: number, payload: PlayPayload): Promise<'ok' | 'conflict'>;
  respond(id: string, action: 'accept' | 'decline'): Promise<PlaySession>;
  cancel(id: string): Promise<void>;
  /**
   * §30.5 — claim the verdict for a finished game.
   *
   * `false` for the 409, which is the ordinary case rather than an error: both clients compute the
   * same ending from the same log, so whichever posts second is confirming what is already there.
   */
  postResult(id: string, result: PlayResult): Promise<boolean>;
}

export class PlayRequestError extends Error {
  readonly status: number;

  constructor(status: number, path: string) {
    super(`${path} answered ${status}`);
    this.name = 'PlayRequestError';
    this.status = status;
  }
}

const BASE = '/api/play';

export function createPlayService(options: PlayServiceOptions = {}): PlayService {
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);

  const json = async <T>(path: string, init?: RequestInit): Promise<T> => {
    const response = await doFetch(`${BASE}${path}`, { credentials: 'same-origin', ...init });
    if (!response.ok) throw new PlayRequestError(response.status, `${BASE}${path}`);
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  };

  const post = (body: unknown): RequestInit => ({
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });

  return {
    create: async (gameId, peer) =>
      (await json<{ session: PlaySession }>('/sessions', post({ gameId, peer }))).session,

    live: async () => (await json<{ sessions: PlaySession[] }>('/sessions')).sessions ?? [],

    read: async (id, since = 0) =>
      (
        await json<{ session: PlaySession }>(
          `/sessions/${encodeURIComponent(id)}?since=${String(since)}`,
        )
      ).session,

    async move(id, seq, payload) {
      const response = await doFetch(`${BASE}/sessions/${encodeURIComponent(id)}/moves`, {
        credentials: 'same-origin',
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ seq, payload }),
      });
      if (response.ok) return 'ok';
      // 409 is the one status that is not an error here — it is the server saying "read the log".
      if (response.status === 409) return 'conflict';
      throw new PlayRequestError(response.status, `${BASE}/sessions/${id}/moves`);
    },

    respond: async (id, action) =>
      (
        await json<{ session: PlaySession }>(
          `/sessions/${encodeURIComponent(id)}/respond`,
          post({ action }),
        )
      ).session,

    cancel: async (id) => {
      await json<void>(`/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
    },

    postResult: async (id, result) => {
      try {
        await json<void>(`/sessions/${encodeURIComponent(id)}/result`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ result }),
        });
        return true;
      } catch (error) {
        if (error instanceof PlayRequestError && error.status === 409) return false;
        throw error;
      }
    },
  };
}
